import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeftIcon } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateAdminMessage,
  useFetchAttachmentUrl,
  useInquiry,
  useUpdateInquiryCategory,
  useUpdateInquiryStatus,
} from '@/lib/query/hooks';
import {
  INQUIRY_CATEGORY_LABELS,
  INQUIRY_SOURCE_PRODUCT_LABELS,
  INQUIRY_STATUS_CHIP_COLORS,
  INQUIRY_STATUS_LABELS,
  formatInquiryDateTime,
  type InquiryAttachmentDto,
  type InquiryCategory,
  type InquiryDetail,
  type InquiryMessageDto,
  type InquiryStatus,
} from '@/lib/types/mise/inquiry';

const STATUSES: InquiryStatus[] = ['open', 'closed'];
// 表示順は mise / loja と統一（質問→不具合→要望→データ→その他）。
const CATEGORIES: InquiryCategory[] = ['question', 'bug', 'feature_request', 'data_issue', 'other'];

export function InquiryDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: inquiry, isLoading, error } = useInquiry(id);

  if (authLoading || !isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
        <CircularProgress />
        <Typography>{authLoading ? '認証状態を確認中...' : 'リダイレクト中...'}</Typography>
      </Box>
    );
  }

  return (
    <AppLayout showSearch={false}>
      <Box sx={{ width: '95%', mx: 'auto', px: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate('/mise/inquiries')} aria-label="戻る">
            <ArrowLeftIcon size={20} />
          </IconButton>
          <Typography variant="h4" component="h1">
            お問い合わせ詳細
          </Typography>
        </Stack>

        {error ? (
          <Alert severity="error">
            {error instanceof Error ? error.message : 'お問い合わせの取得に失敗しました'}
          </Alert>
        ) : isLoading || !inquiry ? (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <InquiryContent inquiry={inquiry} />
        )}
      </Box>
    </AppLayout>
  );
}

function InquiryContent({ inquiry }: { inquiry: InquiryDetail }) {
  return (
    <Stack spacing={2}>
      <InquiryMetaPanel inquiry={inquiry} />
      <StatusCategoryControl inquiry={inquiry} />
      <MessageTimeline inquiry={inquiry} />
      <ReplyForm inquiry={inquiry} />
    </Stack>
  );
}

function InquiryMetaPanel({ inquiry }: { inquiry: InquiryDetail }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={INQUIRY_SOURCE_PRODUCT_LABELS[inquiry.source_product]}
            size="small"
            variant="outlined"
          />
          <Chip
            label={INQUIRY_STATUS_LABELS[inquiry.status]}
            size="small"
            color={INQUIRY_STATUS_CHIP_COLORS[inquiry.status]}
          />
          <Chip label={INQUIRY_CATEGORY_LABELS[inquiry.category]} size="small" variant="outlined" />
        </Stack>
        <Typography variant="h5">{inquiry.subject}</Typography>
        <Divider />
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <MetaItem label="会社 ID" value={inquiry.company_id} />
          <MetaItem label="アカウント ID" value={inquiry.account_id} />
          <MetaItem label="店舗" value={inquiry.seller_name ?? inquiry.seller_id ?? '-'} />
          <MetaItem label="起票者" value={inquiry.user_name ?? inquiry.user_id} />
          <MetaItem label="作成日時" value={formatInquiryDateTime(inquiry.created_at)} />
          <MetaItem label="最終更新" value={formatInquiryDateTime(inquiry.last_message_at)} />
          {inquiry.closed_at ? (
            <MetaItem label="終了日時" value={formatInquiryDateTime(inquiry.closed_at)} />
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function StatusCategoryControl({ inquiry }: { inquiry: InquiryDetail }) {
  const updateStatus = useUpdateInquiryStatus(inquiry.id);
  const updateCategory = useUpdateInquiryCategory(inquiry.id);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={updateStatus.isPending}>
          <InputLabel>ステータス</InputLabel>
          <Select
            value={inquiry.status}
            label="ステータス"
            onChange={(e) => updateStatus.mutate(e.target.value as InquiryStatus)}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {INQUIRY_STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={updateCategory.isPending}>
          <InputLabel>カテゴリ</InputLabel>
          <Select
            value={inquiry.category}
            label="カテゴリ"
            onChange={(e) => updateCategory.mutate(e.target.value as InquiryCategory)}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {INQUIRY_CATEGORY_LABELS[c]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {updateStatus.isError ? (
          <Alert severity="error">ステータスの更新に失敗しました</Alert>
        ) : null}
        {updateCategory.isError ? (
          <Alert severity="error">カテゴリの更新に失敗しました</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

function MessageTimeline({ inquiry }: { inquiry: InquiryDetail }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        {inquiry.messages.length === 0 ? (
          <Typography color="text.secondary">メッセージはありません</Typography>
        ) : (
          inquiry.messages.map((m) => <MessageItem key={m.id} inquiryId={inquiry.id} message={m} />)
        )}
      </Stack>
    </Paper>
  );
}

function MessageItem({ inquiryId, message }: { inquiryId: string; message: InquiryMessageDto }) {
  const isAdmin = message.sender_type === 'admin';
  const senderLabel = isAdmin
    ? `運営: ${message.sender_admin_id ?? '(未設定)'}`
    : `ユーザー: ${message.sender_user_name ?? message.sender_user_id ?? '(不明)'}`;

  return (
    <Box
      sx={{
        bgcolor: isAdmin ? 'primary.50' : 'grey.50',
        borderLeft: '4px solid',
        borderLeftColor: isAdmin ? 'primary.main' : 'grey.400',
        p: 2,
        borderRadius: 1,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {senderLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatInquiryDateTime(message.created_at)}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {message.body}
      </Typography>
      {message.attachments.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {message.attachments.map((a) => (
            <AttachmentChip key={a.id} inquiryId={inquiryId} attachment={a} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function AttachmentChip({
  inquiryId,
  attachment,
}: {
  inquiryId: string;
  attachment: InquiryAttachmentDto;
}) {
  const fetchUrl = useFetchAttachmentUrl();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const url = await fetchUrl(inquiryId, attachment.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('添付ファイルの URL 取得に失敗しました', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Chip
      label={`${attachment.file_name} (${Math.ceil(attachment.file_size / 1024)} KB)`}
      onClick={handleClick}
      disabled={loading}
      variant="outlined"
      size="small"
    />
  );
}

function ReplyForm({ inquiry }: { inquiry: InquiryDetail }) {
  const [body, setBody] = useState('');
  const createMessage = useCreateAdminMessage(inquiry.id);
  const isClosed = inquiry.status === 'closed';

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await createMessage.mutateAsync({ body: trimmed });
      setBody('');
    } catch (e) {
      // エラーは createMessage.isError で表示されるためここでは握り潰さない
      console.error(e);
    }
  };

  if (isClosed) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="info">
          このスレッドは終了しています。返信するにはステータスを変更してください。
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        管理者として返信
      </Typography>
      <TextField
        multiline
        fullWidth
        minRows={3}
        maxRows={10}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="ユーザーへの返信本文を入力 (5000 文字以内)"
        disabled={createMessage.isPending}
        inputProps={{ maxLength: 5000 }}
      />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMessage.isPending || !body.trim()}
        >
          {createMessage.isPending ? '送信中...' : '送信'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          返信ではステータスは変更されません。終了時は上のステータス操作で「終了」に切り替えてください。
        </Typography>
        {createMessage.isError ? (
          <Alert severity="error" sx={{ flexGrow: 1 }}>
            {createMessage.error instanceof Error
              ? createMessage.error.message
              : '返信の送信に失敗しました'}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
